import { Component, ViewEncapsulation, OnDestroy, NgZone } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd  } from '@angular/router';
import { Location } from '@angular/common';

import { AlertController, Platform, NavController, ModalController } from '@ionic/angular';
import { take, filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { get } from 'lodash';
import { isSearchDeepLink } from './services/search-deep-link';
import * as moment from 'moment';
import { Storage } from '@ionic/storage';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { Store } from '@ngrx/store';
import {
  MyPenmatesPage,
  AddPenmatePage,
  LoginPage,
  LoadingModal,
  EmailMessagesModalPage,
  MaintenanceModal,
} from './pages';
import {
  AuthService,
  AppState,
  MyPenmateService,
  EventService,
  LetterService,
  NativeClientService,
  MaintenanceService,
  ClientTelemetryService,
} from './services';
import { MaintenanceState } from './reducers/maintenance.reducer';
import { UserActions, MyPenmateActions, SearchActions, CreateMessageActions } from './actions';

import ErrorHandler from '../lib/error-handler'

declare var Penmate;

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnDestroy {
  modal;
  emailMessagesModal;
  private maintenanceModal;
  private navigationStack: string[] = [];
  private isGoingBack: boolean = false;
  private maintenanceSubscription: Subscription;
  private maintenanceSignature: string;
  private lastMaintenanceState: MaintenanceState;

  constructor(
    private ngZone: NgZone,
    private platform: Platform,
    private splashScreen: SplashScreen,
    private statusBar: StatusBar,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private myPenmateService: MyPenmateService,
    private myPenmateActions: MyPenmateActions,
    private createMessageActions: CreateMessageActions,
    private searchActions: SearchActions,
    private store: Store<AppState>,
    private letterService: LetterService,
    private userActions: UserActions,
    private authService: AuthService,
    private eventService: EventService,
    private nativeService: NativeClientService,
    private navCtrl: NavController,
    private route: ActivatedRoute,
    private storage: Storage,
    private router: Router,
    private location: Location,
    private maintenanceService: MaintenanceService,
    private clientTelemetry: ClientTelemetryService,
  ) {
    this.clientTelemetry.start();
    this.initializeApp();
  }

  initializeApp() {
    this.platform.ready().then(async () => {
      this.statusBar.styleDefault();
      this.splashScreen.hide();
      this.modal = await this.modalCtrl.create({
        component: LoadingModal,
        componentProps: { message: 'Loading...' },
      });
      this.maintenanceService.initialize();
      this.subscribeToMaintenance();

      const url = new URL(window.location.href);
      const paymentIntentClientSecret = url.searchParams.get("setup_intent_client_secret");
      const paymentSucceeded = url.searchParams.get('paymentSucceeded');
      this.eventService.start({ source: 'mobile', path: document.location.pathname });

      this.authService.handleAuthentication().subscribe(this.onHandleAuthentication, error => {
        this.eventService.track('login-error', { source: 'mobile' });
        const online = typeof navigator !== 'undefined' ? navigator.onLine : null;
        const href = typeof window !== 'undefined' ? window.location.href : null;
        const pathname = typeof window !== 'undefined' ? window.location.pathname : null;
        const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
        ErrorHandler.captureMessage('Login / Auth error', {
          error,
          status: error && error.status,
          url: error && error.url,
          online,
          href,
          pathname,
          userAgent,
        });
      });


      this.authService.authenticate().subscribe(({ isAuthenticated, user }) => {
        
        if (isAuthenticated && user) {
          this.store.dispatch(this.myPenmateActions.loadPenmates());
          this.store.dispatch(this.userActions.loginUser(user));
          this.eventService.gaUserData(user.profile_hash)

          const billingRoute = window.location.pathname.match(/billing/i)
          const resetPasswordRoute = window.location.pathname.match(/reset-password/i);
          const reactMessengerRoute = window.location.pathname.match(/penmates\/[^/]+\/messenger/i)
          const legacyMessagingRoute = window.location.pathname.match(/penmates\/[^/]+\/(messages|new-message|new-postcard)(\/|$)/i)
          if (
            resetPasswordRoute ||
            billingRoute ||
            reactMessengerRoute ||
            legacyMessagingRoute ||
            (paymentIntentClientSecret || paymentSucceeded) && window.location.pathname.match(/confirm-order/i)
          ) {
            return;
          }

          try {
            this.storage.get('gclid').then(value => {
              if (!!value) {
                this.eventService.track('add-metrics', { gclid: value });
                this.storage.remove('gclid')
              }
            })
          } catch {/*noop*/}

          if (window.location.pathname.match(/my-account/i)) {
            return;
          }

          const gclidQuery = new URL(window.location.href).searchParams.get('gclid');
          const forwardgClidQuery = user.has_sent_letter && !!gclidQuery;

          if (isSearchDeepLink() && !forwardgClidQuery) {
            return;
          }
          const route = forwardgClidQuery ? `/my-penmates?gclid=${gclidQuery}` : '/my-penmates';
          this.navCtrl.navigateForward(route);
        }
      });
    });

    this.nativeService.data.subscribe(({ type, payload }) => {
      const payloadData = payload || {};
      const { url, state: currentState, caption, author } = payloadData;

      if (type === 'NAVIGATE') {
        return this.navCtrl.navigateForward(url);
      }

      if (type === 'SAFE_AREA_INSETS') {
        try {
          const bottom = Math.max(0, Number(payloadData.bottom) || 0);
          const root = document.documentElement;
          root.style.setProperty('--app-safe-bottom', `${bottom}px`);
          root.style.setProperty('--ion-safe-area-bottom', `${bottom}px`);
          (window as any).__pmNativeSafeAreaBottom = true;
        } catch {
          // ignore bad safe-area payloads
        }
        return;
      }

      if (type === 'ADD_PHOTO') {
        const photo = {
          id: url,
          url,
          caption,
          author,
          selected: true,
          created_at: moment().format(),
          provider: 'instagram',
          source: 'graphql',
          is_carousel: false,
          is_video: false,
        };
        this.store.dispatch(this.createMessageActions.togglePhotoFromGallery(photo));
      }

      if (type === 'DEVICE_INFO') {
        this.store.dispatch(this.userActions.updateDeviceInfo(payloadData));
        const { version, os } = payloadData;
        const versionParts = (version || "").split('.')
        const isIOS =  /ios/i.test(os || '');
        const isAndroid =  /android/i.test(os || '');

        // Add platform class to document for platform-specific CSS
        // iOS RN SafeAreaView already handles safe areas, so we disable env() in CSS
        if (isIOS) {
          document.documentElement.classList.add('rn-ios');
        } else if (isAndroid) {
          document.documentElement.classList.add('rn-android');
        }
        if (!this.nativeService.active || versionParts.length != 3) {
          return;
        }

        this.storage.get('id_token').then(token => {
          this.storage.set('id_token', token || Penmate.env.authToken);
        })
        const minorVersion = parseInt(versionParts[2]);
        const shouldUpgradeiOS = isIOS && minorVersion < 46;
        const shouldUpgradeAndroid =  isAndroid &&  minorVersion < 47
        if (shouldUpgradeiOS || shouldUpgradeAndroid) {
          this.onNotifyAppUpgrade({ isIOS })
        }
      }

      if (type === 'SET_NATIVE_MODULES') {
        this.store.dispatch(this.userActions.setNativeModules(payloadData));
      }

      if (type === 'SUBMIT_RATING_FEEDBACK') {
        this.eventService.track('rating-requested');
        if (!!payloadData && payloadData.showContactForm) {
          this.navCtrl.navigateForward('/my-account', {
            state: { ratingFeedback: payloadData },
          });
        }
      }
    });

    // Track navigation state for React Native back button handling
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      try {
        this.updateNavigationStack(event.url);
        if (this.lastMaintenanceState) {
          this.applyMaintenanceModalState(this.lastMaintenanceState);
        }

        if (this.nativeService.active) {
          const canGoBack = this.canActuallyGoBack();
          
          // Send navigation state to React Native
          this.nativeService.sendMessage({
            type: 'NAVIGATION_STATE_CHANGE',
            payload: {
              canGoBack: canGoBack,
              currentUrl: event.url,
              historyLength: window.history.length,
              stackLength: this.navigationStack.length
            }
          });
        }
      } catch (error) {
        // Fail silently to avoid breaking navigation
      }
    });

    this.watchKeyboard();

    // In-page flows step through same-URL pushState entries, which fire no
    // router or popstate event; they announce each step so the shell's
    // back-button capability stays current.
    window.addEventListener('pm-history-state-changed', () => {
      try {
        if (this.nativeService.active) {
          this.nativeService.sendMessage({
            type: 'NAVIGATION_STATE_CHANGE',
            payload: {
              canGoBack: this.canActuallyGoBack(),
              currentUrl: this.router.url,
              historyLength: window.history.length,
              stackLength: this.navigationStack.length
            }
          });
        }
      } catch (error) {
        // Fail silently
      }
    });

    // Listen to browser back/forward events
    window.addEventListener('popstate', () => {
      try {
        this.isGoingBack = true;
        
        // Small delay to let Angular router update
        setTimeout(() => {
          try {
            this.isGoingBack = false;

            if (this.lastMaintenanceState) {
              this.applyMaintenanceModalState(this.lastMaintenanceState);
            }

            if (this.nativeService.active) {
              const canGoBack = this.canActuallyGoBack();
              
              this.nativeService.sendMessage({
                type: 'NAVIGATION_STATE_CHANGE',
                payload: {
                  canGoBack: canGoBack,
                  currentUrl: this.router.url,
                  historyLength: window.history.length,
                  stackLength: this.navigationStack.length
                }
              });
            }
          } catch (error) {
            // Fail silently
          }
        }, 100);
      } catch (error) {
        this.isGoingBack = false;
      }
    });
  }

  private subscribeToMaintenance() {
    if (this.maintenanceSubscription) {
      return;
    }
    this.maintenanceSubscription = this.maintenanceService.maintenance$.subscribe(state => {
      this.lastMaintenanceState = state;

      if (this.nativeService && this.nativeService.active) {
        const payload = {
          active: !!(state && state.active),
          message: state && state.message ? state.message : null,
          support: state && state.support ? state.support : null,
          retryAfter: state && state.retryAfter ? state.retryAfter : null,
          source: state && state.source ? state.source : null,
        };
        try {
          this.nativeService.sendMessage({
            type: 'MAINTENANCE_STATE_CHANGE',
            payload,
          });
        } catch {
          // ignore messaging errors
        }
      }

      this.applyMaintenanceModalState(state);
    });
  }

  private buildMaintenanceSignature(state: MaintenanceState): string {
    if (!state) {
      return 'inactive';
    }
    return `${state.active}|${state.message || ''}|${state.support || ''}`;
  }

  private shouldSuppressMaintenanceModal(url: string): boolean {
    if (!url) {
      return false;
    }
    if (this.maintenanceService && this.maintenanceService.shouldForceMaintenanceModal && this.maintenanceService.shouldForceMaintenanceModal()) {
      return false;
    }
    const path = (url.split('?')[0] || '').toLowerCase();
    return path === '/login' || path === '/signin' || path === '/' || path === '';
  }

  private applyMaintenanceModalState(state: MaintenanceState) {
    const suppressed = this.shouldSuppressMaintenanceModal(this.router ? this.router.url : '');
    const signature = this.buildMaintenanceSignature(state);

    if (state && state.active) {
      if (suppressed) {
        this.maintenanceSignature = signature;
        this.dismissMaintenanceModal();
        return;
      }

      if (this.maintenanceModal && this.maintenanceSignature === signature) {
        return;
      }

      this.presentMaintenanceModal(state);
      this.maintenanceSignature = signature;
    } else {
      this.maintenanceSignature = null;
      this.dismissMaintenanceModal();
    }
  }

  private async presentMaintenanceModal(state) {
    try {
      if (this.maintenanceModal) {
        await this.maintenanceModal.dismiss();
        this.maintenanceModal = null;
      }
      this.maintenanceModal = await this.modalCtrl.create({
        component: MaintenanceModal,
        componentProps: {
          message: state.message,
          support: state.support,
        },
        backdropDismiss: false,
        cssClass: 'pm-maintenance-modal',
      });
      await this.maintenanceModal.present();
    } catch (error) {
      // Fail silently to avoid interrupting other flows
    }
  }

  private async dismissMaintenanceModal() {
    if (!this.maintenanceModal) {
      return;
    }
    try {
      await this.maintenanceModal.dismiss();
    } catch (error) {
      // noop
    } finally {
      this.maintenanceModal = null;
    }
  }

  onHandleAuthentication = user => {
    this.store
      .select(state => ({
        penmates: state.myPenmates.penmates,
        savedSearch: state.savedSearch,
      }))
      .pipe(take(1))
      .subscribe(({ penmates, savedSearch }) => {

        const resetPasswordRoute = window.location.pathname.match(/reset-password/i);
        const addCreditsRoute = window.location.pathname.match(/add-credits/i);
        const reactMessengerRoute = window.location.pathname.match(/penmates\/[^/]+\/messenger/i);
        const legacyMessagingRoute = window.location.pathname.match(/penmates\/[^/]+\/(messages|new-message|new-postcard)(\/|$)/i);

        const url = new URL(window.location.href);
        const paymentIntentClientSecret = url.searchParams.get("setup_intent_client_secret");
        if (resetPasswordRoute || paymentIntentClientSecret || addCreditsRoute || reactMessengerRoute || legacyMessagingRoute) {
          return;
        }
        
        
        this.eventService.track('login', { source: 'mobile' });
        const userGClid = get(user, 'metrics.gclid')

        if (userGClid && !addCreditsRoute) {
          const gclidSavedQuery = savedSearch && savedSearch.savedSearchQuery;
          if (gclidSavedQuery && gclidSavedQuery.mode) {
            // A committed v2 search draft resumes on the React surface — and must
            // be cleared: the stash persists, and a stale draft would fire a
            // search on a later login.
            this.store.dispatch(this.searchActions.clearSavedSearch());
            return this.navCtrl.navigateForward(`/add-penmate?gclid=${userGClid}`, {
              state: { searchQuery: gclidSavedQuery },
            });
          }
          /* TODO: INCLUDE SAVED (legacy) SEARCH IF PRESENT */
          if (isSearchDeepLink()) {
            return;
          }
          const route = user.has_sent_letter || penmates.length > 0 ? 'my-penmates' : 'find-an-inmate';
          return this.navCtrl.navigateForward(`/${route}?gclid=${userGClid}`);
        }

        if (savedSearch.savedRoute) {
          this.store.dispatch(this.searchActions.clearSavedRoute());
          return this.navCtrl.navigateForward(savedSearch.savedRoute);
        }

        if (savedSearch.savedSearchResult) {
          // Router state is only consumed by the legacy page (preserved under /v1).
          this.navCtrl.navigateForward('/v1/add-penmate', {
            state: { savedSearchResult: savedSearch.savedSearchResult },
          });
          this.eventService.track('view-search-result', savedSearch.savedSearchResult);
          return setTimeout(() => {
            return this.store.dispatch(
              this.searchActions.viewSearchResult(savedSearch.savedSearchResult),
            );
          }, 500);
        }

        this.authService.handleLoginNavigation(this.navCtrl, savedSearch).subscribe(() => {
          this.modalCtrl.getTop().then(modal => {
            if (modal && modal.component === MaintenanceModal) {
              return;
            }
            if (modal) {
              modal.dismiss();
            }
          });
        });
        try {
          this.storage.get('gclid').then(value => {
            if (!!value) {
              this.eventService.track('add-metrics', { gclid: value });
              this.storage.remove('gclid')
            }
          })
        } catch {/*noop*/}
      })
      .unsubscribe();
  };

  onNotifyAppUpgrade = async ({ isIOS }) => {
    if (!this.nativeService || !this.nativeService.active) {
      return;
    }
    const iOSLink = 'https://apps.apple.com/us/app/penmate-send-mail-to-jail/id955317881';
    const AndroidLink = 'market://details?id=com.penmate';
    const appStoreLink = isIOS ? iOSLink : AndroidLink;
    const alert = await this.alertCtrl.create({
      header: 'New Version Available',
      subHeader: 'Please upgrade your app',
      cssClass: 'upgrade-alert app-upgrade-alert',
      message: 'A new version of Penmate is now available. Tap below to upgrade. Please make sure to restart your app after upgrading.',
      backdropDismiss: false,
      buttons: [
        {
          text: 'Upgrade',
          handler: () => {
            this.nativeService.sendMessage({
              type: 'SHOW_EXTERNAL_URL',
              data: { url: appStoreLink },
            });
            return false;
          },
        },
      ],
    });
    await alert.present();
  }

  private updateNavigationStack(url: string): void {
    if (this.isGoingBack) {
      // Going back: pop the last route from stack
      if (this.navigationStack.length > 1) {
        this.navigationStack.pop();
      }
      // console.log('Navigation: BACK to', url, 'Stack now:', this.navigationStack);
    } else {
      // Going forward: add to stack if it's a new route
      const lastUrl = this.navigationStack[this.navigationStack.length - 1];
      
      if (lastUrl !== url) {
        this.navigationStack.push(url);
        // console.log('Navigation: FORWARD to', url, 'Stack now:', this.navigationStack);
      }
    }
  }

  // Marks <body> while the software keyboard is up for a text field, so the
  // shell can give a typing user the screen (the footer nav steps out; see
  // global.scss). "Up" is judged against the tallest visual viewport seen at
  // this width — some browsers shrink the window for the keyboard, others
  // only the visual viewport, and the URL bar's own comings and goings stay
  // under the bar — but only counts while a text field has focus and the
  // page isn't pinch-zoomed (zoom shrinks the visual viewport too). Leaving
  // the field or the page clears it, so the mark can never stick.
  private watchKeyboard(): void {
    const viewport = (window as any).visualViewport;
    if (!viewport || !document.body || !document.body.classList) return;
    let restWidth = 0;
    let restHeight = 0;
    const typing = (): boolean => {
      let el: any = document.activeElement;
      while (el && el.shadowRoot && el.shadowRoot.activeElement) el = el.shadowRoot.activeElement;
      if (!el || !el.tagName) return false;
      if (el.tagName === 'TEXTAREA') return true;
      return el.tagName === 'INPUT' && /^(text|search|email|tel|url|number|password)$/i.test(el.type || 'text');
    };
    const measure = () => {
      const sameWidth = Math.abs(viewport.width - restWidth) < 1;
      // Visual viewport only: Chrome on Android reports a window height that
      // briefly includes the keyboard mid-transition.
      restHeight = Math.max(sameWidth ? restHeight : 0, viewport.height);
      restWidth = viewport.width;
      const zoomed = typeof viewport.scale === 'number' && viewport.scale > 1.05;
      const open = !zoomed && restHeight - viewport.height >= 120 && typing();
      const marked = document.body.classList.contains('pm-keyboard-open');
      if (open && !marked) document.body.classList.add('pm-keyboard-open');
      if (!open && marked) {
        document.body.classList.remove('pm-keyboard-open');
        // Chrome on Android can leave the fixed body at its keyboard-era
        // height when nothing else changes as the keyboard goes; a hair of
        // geometry change on the app container makes it lay out again.
        const app = document.querySelector('ion-app') as HTMLElement | null;
        if (app) {
          app.style.bottom = '0.01px';
          requestAnimationFrame(() => { app.style.bottom = ''; });
        }
      }
    };
    // The keyboard's own resize clears the mark (so the style change lands
    // together with the viewport growing back); blur only re-checks late,
    // after the keyboard has had time to go, in case no resize follows.
    // Outside the zone: the viewport resizes on every frame of the keyboard
    // animation, and nothing here needs change detection. These live as long
    // as the app does.
    this.ngZone.runOutsideAngular(() => {
      viewport.addEventListener('resize', measure);
      window.addEventListener('resize', measure);
      document.addEventListener('focusin', measure);
      document.addEventListener('focusout', () => setTimeout(measure, 600));
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => measure());
      measure();
    });
  }

  private canActuallyGoBack(): boolean {
    try {
      // The most reliable way: test if browser can actually go back
      // We do this by checking if the current history position allows going back
      const historyLength = window.history.length;
      const canUseHistory = historyLength > 1;
      
      // Additional check: if we only have 1 route in our stack, we're at the start
      const atStartOfApp = this.navigationStack.length <= 1;

      // An in-page flow keeps its steps as same-URL history entries the router
      // stack can't see; while one is deeper than its first step, back has
      // somewhere to go even on a cold deep link.
      const state = window.history.state;
      const inFlowDepth =
        state && state.pmSearch && typeof state.pmDepth === 'number' ? state.pmDepth : 0;
      if (inFlowDepth > 0) {
        return true;
      }
      
      // console.log('canGoBack check:', {
      //   historyLength,
      //   stackLength: this.navigationStack.length,
      //   atStartOfApp,
      //   result: canUseHistory && !atStartOfApp
      // });
      
      return canUseHistory && !atStartOfApp;
    } catch (error) {
      // console.log('canGoBack error, defaulting to false:', error);
      return false;
    }
  }

  ngOnDestroy() {
    if (this.maintenanceSubscription) {
      this.maintenanceSubscription.unsubscribe();
    }
    this.maintenanceService.stopPolling();
    this.dismissMaintenanceModal();
  }
}
